// Execution progress UI (BLD-17): compact bar for lists, gauge for headers, and the follow-up panel on the
// initiative page where axis editors and the instructor record completion per action.
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Panel, Skeleton, ErrorState, cx } from "./ui.tsx";
import { useToast } from "./toast.tsx";
import { ApiError, get, post } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { useUnsavedGuard } from "../lib/guard.tsx";
import type { ProgressDetail } from "../lib/types.ts";
import { PROGRESS_OPTIONS, initiativeProgress } from "../../shared/progress.ts";
import { formatDateBR, formatInstantBR } from "../../shared/dates.ts";
import { actionLabel } from "../../shared/numbering.ts";

/** Thin horizontal bar with the percentage; `label` is read by screen readers. */
export function ProgressBar({ value, color = "#26B9A9", label, className }: { value: number; color?: string; label: string; className?: string }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <span className={cx("inline-flex items-center gap-2", className)}>
      <span role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={v}
        className="relative h-1.5 w-20 overflow-hidden rounded-full bg-line">
        <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${v}%`, background: color }} />
      </span>
      <span className="num text-xs font-medium">{v}%</span>
    </span>
  );
}

/** Semicircle gauge (same shape as the synthetic report). */
export function ProgressGauge({ value, color = "#26B9A9", size = 96 }: { value: number; color?: string; size?: number }) {
  const v = Math.max(0, Math.min(100, value));
  const r = 40;
  const arc = Math.PI * r;
  return (
    <svg viewBox="0 0 100 58" width={size} height={size * 0.58} role="img" aria-label={`Execução: ${v}%`}>
      <path d="M10 50 A40 40 0 0 1 90 50" fill="none" stroke="#E3E9EA" strokeWidth="10" strokeLinecap="round" />
      <path d="M10 50 A40 40 0 0 1 90 50" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={`${(arc * v) / 100} ${arc}`} />
      <text x="50" y="49" textAnchor="middle" fontSize="16" fontWeight="500" fill="#13253D" style={{ fontFamily: "var(--font-mono)" }}>{v}%</text>
    </svg>
  );
}

export function ProgressPanel({ initiativeId, color }: { initiativeId: string; color: string }) {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const key = planKey(plan.id, "progress", initiativeId);
  const q = useQuery({ queryKey: key, queryFn: async () => (await get<{ data: ProgressDetail }>(`/plans/${plan.id}/initiatives/${initiativeId}/progress`)).data });
  // Only the percentages the viewer changed; everything else shows the saved value.
  const [edits, setEdits] = useState<Record<string, number>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  // One Idempotency-Key per logical save, reused if the same save is retried after a network failure.
  const attempt = useRef<string | null>(null);

  const changed = useMemo(() => (q.data ? q.data.actions.filter((a) => edits[a.id] !== undefined && edits[a.id] !== a.percent) : []), [q.data, edits]);
  const dirty = changed.length > 0 || note.trim() !== "";
  useUnsavedGuard(dirty);

  if (q.isPending) return <Panel><Skeleton lines={3} /></Panel>;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const preview = initiativeProgress(d.actions.map((a) => ({ weight: a.weight, percent: edits[a.id] ?? a.percent })));

  async function save() {
    if (!dirty) return;
    setBusy(true);
    setConflict(null);
    attempt.current ??= crypto.randomUUID();
    try {
      const updates = (changed.length ? changed : d.actions.slice(0, 1)).map((a) => ({ action_id: a.id, percent: edits[a.id] ?? a.percent, expected_version: a.version }));
      const res = await post<{ data: ProgressDetail }>(`/plans/${plan.id}/initiatives/${initiativeId}/progress`, { updates, note: note.trim() || undefined }, { "Idempotency-Key": attempt.current });
      qc.setQueryData(key, res.data);
      void qc.invalidateQueries({ queryKey: planKey(plan.id, "structure") });
      void qc.invalidateQueries({ queryKey: planKey(plan.id, "dashboard") });
      attempt.current = null;
      setEdits({});
      setNote("");
      toast("success", "Acompanhamento salvo.");
    } catch (e) {
      attempt.current = null;
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") setConflict(e.message);
      else toast("danger", e instanceof Error ? e.message : "Não foi possível salvar o acompanhamento.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel className="mb-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold">Acompanhamento da execução</h2>
          <p className="mt-0.5 max-w-xl text-sm text-muted">
            Informe quanto de cada ação já foi realizado. A execução da iniciativa é a soma ponderada pelos pesos.
          </p>
        </div>
        <ProgressGauge value={dirty ? preview : d.percent} color={color} />
      </div>
      {d.readonly_reason && <Alert tone="info">{d.readonly_reason}</Alert>}
      {conflict && (
        <Alert tone="warn" title="Acompanhamento desatualizado" action={<Button variant="secondary" onClick={() => { setConflict(null); setEdits({}); void q.refetch(); }}>Carregar valores atuais</Button>}>
          {conflict}
        </Alert>
      )}
      <ul className="mt-3 divide-y divide-line">
        {d.actions.map((a) => (
          <li key={a.id} className="grid gap-x-4 gap-y-1 py-2.5 md:grid-cols-[minmax(0,1fr)_7rem_9rem] md:items-center">
            <span className="min-w-0 text-sm">
              <span className="font-semibold text-muted">{actionLabel(a.number)}</span> · {a.description ?? <span className="text-muted italic">a definir</span>}
              <span className="block text-xs text-muted">
                Peso {a.weight ?? "—"} · {formatDateBR(a.starts_on) || "—"} a {formatDateBR(a.ends_on) || "—"}
                {a.updated_at && ` · atualizado por ${a.updated_by ?? "—"} em ${formatInstantBR(a.updated_at)}`}
              </span>
            </span>
            <ProgressBar value={edits[a.id] ?? a.percent} color={color} label={`${actionLabel(a.number)}: realizado`} className="md:justify-self-start" />
            {d.can_update ? (
              <label className="flex items-center gap-2 text-sm">
                <span className="sr-only">{actionLabel(a.number)}: percentual realizado</span>
                <select className="min-h-10 w-full rounded-ctl border border-field bg-white px-2 text-[15px]"
                  value={edits[a.id] ?? a.percent} onChange={(e) => setEdits({ ...edits, [a.id]: Number(e.target.value) })}>
                  {PROGRESS_OPTIONS.map((p) => <option key={p} value={p}>{p}% realizado</option>)}
                </select>
              </label>
            ) : <span className="text-sm text-muted md:text-right">{a.percent}% realizado</span>}
          </li>
        ))}
      </ul>
      {d.can_update && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="min-w-0 flex-1 text-sm">
            <span className="mb-1 block text-[13px] font-semibold">Observação (opcional)</span>
            <input className="block min-h-10 w-full rounded-ctl border border-field bg-white px-3 text-[15px]" maxLength={500}
              value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: levantamento concluído em 3 unidades" />
          </label>
          <Button onClick={() => void save()} loading={busy} disabled={!dirty}>Salvar acompanhamento</Button>
        </div>
      )}
      {d.history.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-semibold text-primary">Histórico do acompanhamento ({d.history.length})</summary>
          <ul className="mt-2 space-y-2 text-sm">
            {d.history.map((h) => (
              <li key={h.id} className="rounded-ctl bg-paper px-3 py-2">
                <span className="font-semibold">{h.metadata.percent_from ?? 0}% → {h.metadata.percent_to ?? 0}%</span>
                <span className="text-muted"> · {h.actor_name ?? "—"} em {formatInstantBR(h.occurred_at)}</span>
                {h.metadata.changes && h.metadata.changes.length > 0 && (
                  <span className="block text-xs text-muted">{h.metadata.changes.map((c) => `${actionLabel(c.number)}: ${c.from}% → ${c.to}%`).join(" · ")}</span>
                )}
                {h.metadata.note && <span className="block">{h.metadata.note}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Panel>
  );
}
